import { JTable } from '@atoms/JTable';
import TableColumn from '@models/table-column';
import { useGetCvVideoSteps } from '@settings/queries/useGetVideoSteps';
import { useTranslation } from 'react-i18next';
import { CvVideoStepRow } from '../CvVideoStepRow';
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from 'react-beautiful-dnd';
import { useEffect, useState } from 'react';
import GetVideoStepItemDto from '@settings/models/dto/get-video-step-item.dto';
import { useUpdateVideoStepOrderMutation } from '@settings/mutations/videoStepsMutations';
import { InputAdornment, Stack, useTheme } from '@mui/material';
import { JButton } from '@atoms/JButton';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { IconSize } from '@constants';
import useJDialog from '@/hooks/useJDialog';
import CvVideoStepForm from '../CvVideoStepForm';

import JTextFieldBase from '@atoms/JTextFieldBase';

const TABLE_COLS: TableColumn[] = [
  { name: '', orderable: false, sortingField: '' },
  { name: 'TITLE', orderable: false, sortingField: '' },
  { name: 'STEP', orderable: false, sortingField: '' },
  { name: 'DURATION_STEP', orderable: false, sortingField: '' },
  { name: 'REQUIRED_STEP', orderable: false, sortingField: '' },
  { name: 'TO_DO', orderable: false, sortingField: '' },
  { name: 'TO_AVOID', orderable: false, sortingField: '' },
  { name: 'STATUS', orderable: false, sortingField: '' },
];

const CvVideosStepsSettings = () => {
  const { data, isLoading } = useGetCvVideoSteps();
  const { t } = useTranslation();
  const theme = useTheme();
  const { showJDialog } = useJDialog();
  const { mutate: updateRank } = useUpdateVideoStepOrderMutation();

  const [rows, setRows] = useState<GetVideoStepItemDto[]>([]);
  const [keyWord, setKeyWord] = useState<string>('');

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return; // Item was dropped outside the list

    const updatedRows = [...rows];
    const [movedItem] = updatedRows.splice(result.source.index, 1);
    updatedRows.splice(result.destination.index, 0, movedItem);

    // Calculate the correct rank for each item in the updated list
    updatedRows.forEach((item, index) => {
      item.rank = index + 1;
    });

    setRows(updatedRows); // Update the local order

    // Calculate the changed items with their new ranks

    const changedItems = updatedRows.slice(
      Math.min(result.source.index, result.destination.index),
      Math.max(result.source.index, result.destination.index) + 1,
    );
    if (changedItems.length <= 1) return;
    updateRank(changedItems.map((item) => ({ id: item.id, rank: item.rank })));
  };

  const isNotFound = rows?.length === 0;
  useEffect(() => {
    if (isLoading || !data?.data) return;
    setRows(data?.data.sort((a, b) => a.rank - b.rank));
  }, [isLoading, data?.data]);

  const openCreateDialog = () => {
    showJDialog(
      t('NEW_STEP'),
      <CvVideoStepForm defaultRank={rows.length + 1} />,
      'filter',
    );
  };

  return (
    <>
      <Stack
        mb={2}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <JTextFieldBase
          attr={{ whiteBg: true }}
          size="small"
          name="title"
          label={t('TITLE') ?? ''}
          onChange={(e) => setKeyWord(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <MagnifyingGlassIcon
                  color={theme.palette.primary.main}
                  width={IconSize.sm}
                />
              </InputAdornment>
            ),
          }}
        />
        <JButton
          onClick={openCreateDialog}
          startIcon={
            <PlusIcon
              color={theme.palette.white.main}
              height={IconSize.buttonIcon}
            />
          }
          variant="contained"
          color="secondary"
        >
          {t('NEW_STEP_2')}
        </JButton>
      </Stack>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(providedDrop) => {
            return (
              <JTable
                providedDrop={providedDrop}
                isNotFound={isNotFound}
                columns={TABLE_COLS}
                notFoundLabel={t('NOT_FOUND') ?? ''}
              >
                {rows
                  ?.filter((item) =>
                    item.name.toLowerCase().includes(keyWord.toLowerCase()),
                  )
                  .map((row, index) => (
                    <Draggable key={row.id} draggableId={row.id} index={index}>
                      {(providedDrag, snapshot) => {
                        return (
                          <CvVideoStepRow
                            providedDrag={providedDrag}
                            row={row}
                            key={row.id}
                          />
                        );
                      }}
                    </Draggable>
                  ))}
                {providedDrop.placeholder}
              </JTable>
            );
          }}
        </Droppable>
      </DragDropContext>
    </>
  );
};

export default CvVideosStepsSettings;
